import { SERVER_CONTACT_EVENT } from './server-contact';

// Reports interaction so reading counts as presence; signs out a window after
// the last gesture. Inert unless the body carries data-session-window (gated).

// Margin buys a second attempt, so one lost packet cannot cost a member their
// half-filled form.
const PING_MARGIN_MS = 120_000;
const RETRY_AFTER_MS = 60_000;

// Floor on the scheduler so a very short window cannot busy-loop.
const MIN_TIMER_MS = 1_000;

const HEARTBEAT_PATH = '/session-heartbeat';
const EXPIRED_PATH = '/auth/session-expired';

// Exported for tests; the bootstrap below is what production uses.
// Gestures that count as a pulse. Passive so scroll performance is untouched.
const INTERACTION_EVENTS = [
    'pointerdown',
    'keydown',
    'scroll',
    'touchstart'
] as const;

// Shared across this origin's tabs: gestures do not cross tabs, so an idle
// second tab would sign the member out of the one they are working in.
const SHARED_INTERACTION_KEY = 'soda.mp.lastInteraction';

// localStorage writes are synchronous and scroll fires per frame, so persist
// rarely. The slop only delays another tab noticing, which the window absorbs.
const PERSIST_EVERY_MS = 5_000;

export class SessionHeartbeat {
    private readonly windowMs: number;
    private lastInteraction = Date.now();
    private lastServerContact = Date.now();
    private lastPersist = 0;
    private timer: number | undefined;
    private signingOut = false;

    constructor(
        windowSeconds: number,
        private readonly csrfToken: string
    ) {
        this.windowMs = windowSeconds * 1000;
    }

    start(): void {
        // Capture: scroll does not bubble, so without it a gesture inside an
        // overflow container reads as absence. Passive keeps scrolling smooth.
        for (const event of INTERACTION_EVENTS) {
            document.addEventListener(event, this.noteInteraction, {
                capture: true,
                passive: true
            });
        }

        // A request rolls the window server-side, so it counts as contact and
        // defers the next ping.
        for (const event of ['htmx:afterRequest', SERVER_CONTACT_EVENT]) {
            document.body.addEventListener(event, this.noteServerContact);
        }

        this.schedule();
    }

    private noteServerContact = (): void => {
        this.lastServerContact = Date.now();
        this.schedule();
    };

    private noteInteraction = (): void => {
        const now = Date.now();
        this.lastInteraction = now;
        if (now - this.lastPersist < PERSIST_EVERY_MS) return;
        this.lastPersist = now;
        try {
            localStorage.setItem(SHARED_INTERACTION_KEY, String(now));
        } catch {
            // No storage: this tab still tracks its own gestures.
        }
    };

    // The latest gesture in any tab. A future value is discarded, not clamped:
    // clamping reads as "interacted just now" and pins the session open.
    private lastInteractionAnyTab(now: number): number {
        try {
            const shared = Number(localStorage.getItem(SHARED_INTERACTION_KEY));
            if (
                Number.isFinite(shared) &&
                shared > this.lastInteraction &&
                shared <= now
            ) {
                return shared;
            }
        } catch {
            // No storage: fall back to this tab alone.
        }
        return this.lastInteraction;
    }

    // Reads the clocks on each wake-up, so a throttled background timer cannot
    // buy extra minutes: whenever it fires, the elapsed time is still true.
    private check = (): void => {
        const now = Date.now();

        if (now - this.lastInteractionAnyTab(now) >= this.windowMs) {
            this.expire();
            return;
        }

        // No gesture since last contact means our deadline is no later than the
        // server's, so a ping could only prolong a session we are about to end.
        if (this.lastInteractionAnyTab(now) <= this.lastServerContact) {
            this.schedule();
            return;
        }

        if (now - this.lastServerContact >= this.windowMs - PING_MARGIN_MS) {
            void this.ping(true);
            return;
        }

        this.schedule();
    };

    // Wake at whichever deadline comes first.
    private schedule(): void {
        window.clearTimeout(this.timer);

        const now = Date.now();
        const untilExpiry =
            this.lastInteractionAnyTab(now) + this.windowMs - now;
        const untilPing =
            this.lastServerContact + this.windowMs - PING_MARGIN_MS - now;

        this.timer = window.setTimeout(
            this.check,
            Math.max(Math.min(untilExpiry, untilPing), MIN_TIMER_MS)
        );
    }

    // Only reached inside the member's window, so it cannot revive a dead
    // session. A failure retries, then the normal schedule resumes.
    private async ping(mayRetry: boolean): Promise<void> {
        try {
            const response = await fetch(HEARTBEAT_PATH, {
                method: 'POST',
                // Same CSRF middleware as every form; without the token
                // this 403s and the session expires under an active member.
                headers: {
                    'X-Requested-With': 'session-heartbeat',
                    'X-CSRF-Token': this.csrfToken
                },
                credentials: 'same-origin'
            });
            if (!response.ok) throw new Error(`heartbeat ${response.status}`);
            this.lastServerContact = Date.now();
            this.schedule();
        } catch {
            // A retry must never wake past the member's deadline, or a
            // failing network silently extends the window.
            window.clearTimeout(this.timer);
            const now = Date.now();
            const untilExpiry =
                this.lastInteractionAnyTab(now) + this.windowMs - now;

            if (!mayRetry || untilExpiry <= RETRY_AFTER_MS) {
                this.timer = window.setTimeout(
                    this.check,
                    Math.max(untilExpiry, MIN_TIMER_MS)
                );
                return;
            }
            this.timer = window.setTimeout(
                () => void this.ping(false),
                RETRY_AFTER_MS
            );
        }
    }

    // Navigate rather than leave a stale page for the member to click into.
    private expire(): void {
        if (this.signingOut) return;
        this.signingOut = true;
        window.location.assign(EXPIRED_PATH);
    }
}

const windowSeconds = Number(document.body?.dataset.sessionWindow);
const csrfToken = document.body?.dataset.csrfToken ?? '';
if (Number.isFinite(windowSeconds) && windowSeconds > 0) {
    new SessionHeartbeat(windowSeconds, csrfToken).start();
}
